import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, fmtIST } from "../lib/api";
import Shell, { PageHeader } from "../components/Shell";
import { Plus, CheckCircle, Scales } from "@phosphor-icons/react";

export default function Reco() {
  const { storeId } = useParams();
  const [tab, setTab] = useState("new");
  const [inv, setInv] = useState([]);
  const [counts, setCounts] = useState({});
  const [history, setHistory] = useState([]);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    const [a, b] = await Promise.all([
      api.get(`/store/${storeId}/inventory`),
      api.get(`/store/${storeId}/recos`),
    ]);
    setInv(a.data);
    setHistory(b.data);
    const c = {};
    for (const it of a.data) c[it.sku] = it.qty;
    setCounts(c);
  };
  useEffect(() => { load(); }, [storeId]);

  const save = async () => {
    const { data } = await api.post("/store/reco", { store_id: storeId, counts });
    alert(`Reco saved with ${data.lines.length} lines. Variances captured.`);
    load();
    setTab("history");
  };

  const apply = async (r) => {
    if (!window.confirm("Apply physical counts to inventory? This will overwrite current stock.")) return;
    await api.post(`/store/reco/${r.id}/apply`);
    alert("Applied to inventory.");
    load();
  };

  return (
    <Shell>
      <PageHeader
        title="Inventory Reconciliation"
        subtitle="Compare physical count against system stock. Apply to correct variance."
        testId="reco-header"
      />
      <div className="p-8 space-y-4">
        <div className="inline-flex bg-white border border-slate-200 rounded-md p-1">
          {[["new", "New Reco"], ["history", "History"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`reco-tab-${k}`} className={`px-4 h-9 text-xs font-semibold rounded-sm uppercase tracking-wider ${tab === k ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}>{l}</button>
          ))}
        </div>

        {tab === "new" && (
          <>
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm" data-testid="reco-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                    <th className="text-left px-4 py-3 font-semibold">SKU</th>
                    <th className="text-left px-4 py-3 font-semibold">Product</th>
                    <th className="text-right px-4 py-3 font-semibold">Expected</th>
                    <th className="text-right px-4 py-3 font-semibold">Physical Count</th>
                    <th className="text-right px-4 py-3 font-semibold">Variance</th>
                  </tr>
                </thead>
                <tbody>
                  {inv.map((s) => {
                    const ph = counts[s.sku] ?? 0;
                    const vari = ph - s.qty;
                    return (
                      <tr key={s.sku} className="border-b border-slate-100">
                        <td className="px-4 py-2 font-mono-num">{s.sku}</td>
                        <td className="px-4 py-2">{s.product_name}</td>
                        <td className="px-4 py-2 text-right font-mono-num">{s.qty}</td>
                        <td className="px-4 py-2 text-right">
                          <input type="number" value={counts[s.sku] ?? 0} onChange={(e) => setCounts({ ...counts, [s.sku]: parseInt(e.target.value) || 0 })} data-testid={`count-${s.sku}`} className="w-20 h-8 px-2 border border-slate-300 rounded-md text-xs font-mono-num text-right" />
                        </td>
                        <td className={`px-4 py-2 text-right font-mono-num font-semibold ${vari === 0 ? "text-slate-400" : vari < 0 ? "text-red-600" : "text-emerald-600"}`}>{vari > 0 ? `+${vari}` : vari}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button onClick={save} data-testid="save-reco" className="h-11 px-6 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-md text-sm inline-flex items-center gap-2"><Scales size={16} weight="bold" />Save Reconciliation</button>
          </>
        )}

        {tab === "history" && (
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-right px-4 py-3">Lines</th>
                  <th className="text-right px-4 py-3">Variance Units</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-slate-400">No reconciliations yet.</td></tr>}
                {history.map((r) => {
                  const totalVar = r.lines.reduce((a, l) => a + Math.abs(l.variance), 0);
                  return (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono-num">{fmtIST(r.created_at)}</td>
                      <td className="px-4 py-3 text-right font-mono-num">{r.lines.length}</td>
                      <td className="px-4 py-3 text-right font-mono-num font-semibold">{totalVar}</td>
                      <td className="px-4 py-3">
                        {r.applied ? <span className="text-emerald-700 text-xs font-semibold uppercase tracking-wider">Applied</span> : <span className="text-amber-700 text-xs font-semibold uppercase tracking-wider">Pending</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {!r.applied && <button onClick={() => apply(r)} data-testid={`apply-reco-${r.id}`} className="h-9 px-3 text-xs font-semibold bg-slate-900 text-white rounded-md hover:bg-slate-800 inline-flex items-center gap-1"><CheckCircle size={12} weight="bold" />Apply</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Shell>
  );
}
